import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import {
  SprayCan,
  Snowflake,
  Key,
  Leaf,
  CloudSnow,
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  ArrowRight,
  Clock,
  Shield,
  Award,
  Star,
  Hammer,
  Sparkles
} from 'lucide-react';
import { mockData } from '../mock';
import ChatBooking from '../components/ChatBooking';
import Testimonials from '../components/Testimonials';
import FAQ from '../components/FAQ';
import Logo from '../components/Logo';

const iconMap = {
  wrench: Hammer,
  'spray-can': SprayCan,
  snowflake: Snowflake,
  key: Key,
  leaf: Leaf,
  'cloud-snow': CloudSnow
};

const Home = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Takk for din henvendelse!",
      description: "Vi tar kontakt med deg så snart som mulig.",
    });
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-xl shadow-sm sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <a href="/" className="flex items-center gap-3 group">
              <Logo className="w-10 h-10 md:w-12 md:h-12" />
              <div>
                <span className="text-lg md:text-xl font-bold text-slate-900 block leading-tight">Iversen</span>
                <span className="text-xs text-slate-500 font-medium">Drift & Vedlikehold</span>
              </div>
            </a>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#tjenester" className="text-slate-700 hover:text-blue-600 font-medium transition-colors relative group">
                Tjenester
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600 group-hover:w-full transition-all duration-300"></span>
              </a>
              <a href="#om-oss" className="text-slate-700 hover:text-blue-600 font-medium transition-colors relative group">
                Om oss
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600 group-hover:w-full transition-all duration-300"></span>
              </a>
              <a href="#kontakt" className="text-slate-700 hover:text-blue-600 font-medium transition-colors relative group">
                Kontakt
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600 group-hover:w-full transition-all duration-300"></span>
              </a>
              <a href="#chat-booking">
                <Button className="bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-lg transition-all">
                  Book time
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-16 md:py-28 overflow-hidden bg-slate-50">
        <div className="absolute inset-0">
          <div className="absolute top-10 right-0 w-80 h-80 md:w-[500px] md:h-[500px] bg-blue-500/8 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 md:w-[600px] md:h-[600px] bg-cyan-500/8 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white border border-slate-200 px-4 py-2 rounded-full mb-6 shadow-sm">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-semibold text-slate-700">Tilgjengelig i Bergen</span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-slate-900 mb-6 leading-[1.1] px-4">
              <span className="block mb-2">Din vaktmester i Bergen</span>
              <span className="bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-500 bg-clip-text text-transparent">
                som alltid leverer
              </span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed px-4">
              Fra småting til større oppdrag – vi fikser det meste med kvalitet og profesjonalitet
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center px-4">
              <a href="#chat-booking" className="w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white text-base md:text-lg px-8 md:px-10 py-6 md:py-7 rounded-xl shadow-lg hover:shadow-xl transition-all hover:scale-[1.02]">
                  Start chat for booking
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <a href="tel:+4712345678" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="w-full sm:w-auto text-base md:text-lg px-8 md:px-10 py-6 md:py-7 rounded-xl border-2 border-slate-300 hover:border-blue-600 hover:bg-blue-50 transition-all hover:scale-[1.02] shadow-sm">
                  <Phone className="mr-2 h-5 w-5" />
                  Ring oss
                </Button>
              </a>
            </div>
            
            {/* Trust indicators */}
            <div className="mt-16 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-4xl mx-auto px-4">
              <div className="bg-white p-5 md:p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                <div className="w-14 h-14 md:w-16 md:h-16 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Clock className="h-7 w-7 md:h-8 md:w-8 text-white" />
                </div>
                <div className="text-2xl md:text-3xl font-bold text-slate-900">Rask</div>
                <div className="text-sm text-slate-600">Respons</div>
              </div>
              <div className="bg-white p-5 md:p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                <div className="w-14 h-14 md:w-16 md:h-16 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Shield className="h-7 w-7 md:h-8 md:w-8 text-white" />
                </div>
                <div className="text-2xl md:text-3xl font-bold text-slate-900">100%</div>
                <div className="text-sm text-slate-600">Kvalitet</div>
              </div>
              <div className="bg-white p-5 md:p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                <div className="w-14 h-14 md:w-16 md:h-16 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Award className="h-7 w-7 md:h-8 md:w-8 text-white" />
                </div>
                <div className="text-2xl md:text-3xl font-bold text-slate-900">10+</div>
                <div className="text-sm text-slate-600">År erfaring</div>
              </div>
              <div className="bg-white p-5 md:p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                <div className="w-14 h-14 md:w-16 md:h-16 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Star className="h-7 w-7 md:h-8 md:w-8 text-white" />
                </div>
                <div className="text-2xl md:text-3xl font-bold text-slate-900">5.0</div>
                <div className="text-sm text-slate-600">Vurdering</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chat Booking Section - Right after Hero */}
      <ChatBooking />

      {/* Services Section */}
      <section id="tjenester" className="py-16 md:py-24 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 md:w-96 md:h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-12 md:mb-16">
            <span className="text-cyan-400 font-bold text-xs md:text-sm uppercase tracking-wider">Våre tjenester</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold mt-4 mb-4 px-4">
              Alt du trenger av vaktmestertjenester
            </h2>
            <p className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto px-4">
              Profesjonelle tjenester for både private og bedrifter i Bergen
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockData.services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card
                  key={service.id}
                  className={`group relative overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 border ${
                    service.available 
                      ? 'border-slate-700 hover:border-cyan-400 bg-slate-800/80 backdrop-blur-sm' 
                      : 'border-slate-700/50 bg-slate-800/40 opacity-60'
                  }`}
                >
                  {service.available && (
                    <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  )}
                  <CardHeader className="relative pb-4">
                    <div className={`w-16 h-16 md:w-18 md:h-18 rounded-xl flex items-center justify-center mb-5 shadow-lg transition-all group-hover:scale-110 ${
                      service.available 
                        ? 'bg-blue-600' 
                        : 'bg-slate-700'
                    }`}>
                      <IconComponent className={`h-8 w-8 md:h-9 md:w-9 ${
                        service.available ? 'text-white' : 'text-slate-500'
                      }`} />
                    </div>
                    <CardTitle className="text-xl md:text-2xl font-bold text-white">
                      {service.title}
                      {!service.available && (
                        <span className="block md:inline md:ml-3 mt-2 md:mt-0 text-xs font-semibold text-slate-400 bg-slate-700 px-3 py-1.5 rounded-full">
                          Kommer snart
                        </span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="relative">
                    <CardDescription className="text-sm md:text-base text-slate-300 leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 md:py-24 bg-white relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-12 md:mb-16">
            <span className="text-blue-600 font-bold text-xs md:text-sm uppercase tracking-wider">Hvorfor velge oss?</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 mt-4 mb-4 px-4">Din pålitelige partner</h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto px-4">Vi leverer kvalitet og profesjonalitet i hver eneste jobb</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {mockData.features.map((feature, index) => (
              <div key={index} className="group">
                <div className="p-6 md:p-8 bg-slate-50 hover:bg-blue-50 rounded-2xl border border-slate-200 hover:border-blue-300 transition-all hover:shadow-lg">
                  <CheckCircle2 className="h-12 w-12 md:h-14 md:w-14 text-blue-600 mb-5 group-hover:scale-110 transition-transform" />
                  <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                  <p className="text-sm md:text-base text-slate-600 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* FAQ */}
      <FAQ />

      {/* About Section */}
      <section id="om-oss" className="py-16 md:py-24 bg-slate-50 relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <span className="text-blue-600 font-bold text-xs md:text-sm uppercase tracking-wider">Om oss</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 mt-4 mb-8 px-4">
              Profesjonell vaktmester i Bergen
            </h2>
            <div className="space-y-5 text-base md:text-lg text-slate-600 leading-relaxed">
              <p>
                Iversen Drift & Vedlikehold er din lokale og pålitelige partner for alle typer vaktmester- og vedlikeholdsoppgaver i Bergen og omegn.
              </p>
              <p>
                Med solid erfaring og en genuin omsorg for kvalitet tar vi oss av alt fra små reparasjoner til større vedlikeholdsoppdrag. Vi jobber alltid for å levere førsteklasses service til konkurransedyktige priser.
              </p>
              <p>
                Vi utvider kontinuerlig vårt tjenestetilbud. Snart tilbyr vi også gressklipping og snørydding når vi får bil.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="kontakt" className="py-16 md:py-24 bg-white relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-12 md:mb-16">
            <span className="text-blue-600 font-bold text-xs md:text-sm uppercase tracking-wider">Kontakt oss</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 mt-4 mb-4 px-4">Ta kontakt i dag</h2>
            <p className="text-base md:text-lg text-slate-600 px-4">Vi gir deg et uforpliktende tilbud innen 24 timer</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            {/* Contact Form */}
            <Card className="shadow-xl border-2 border-slate-200">
              <CardHeader className="pb-6">
                <CardTitle className="text-2xl">Send oss en melding</CardTitle>
                <CardDescription className="text-base">Vi svarer så snart som mulig</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="name" className="text-base font-semibold">Navn *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Ditt fulle navn"
                      required
                      className="mt-2 h-12 text-base border-2 border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-base font-semibold">E-post *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="din@epost.no"
                      required
                      className="mt-2 h-12 text-base border-2 border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-base font-semibold">Telefon *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+47 123 45 678"
                      required
                      className="mt-2 h-12 text-base border-2 border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    />
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-base font-semibold">Melding *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Beskriv hva du trenger hjelp til, og når det passer best..."
                      required
                      rows={5}
                      className="mt-2 text-base border-2 border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    />
                  </div>
                  <Button type="submit" className="w-full h-14 text-lg bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-lg transition-all hover:scale-[1.02]">
                    Send melding
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <div className="space-y-6">
              <Card className="shadow-lg border-2 border-slate-200">
                <CardHeader>
                  <CardTitle className="text-2xl">Kontaktinformasjon</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <a href="tel:+4712345678" className="flex items-start gap-4 group hover:bg-slate-50 p-3 rounded-xl transition-colors">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-blue-600 transition-colors">
                      <Phone className="h-6 w-6 text-blue-600 group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-900 text-lg">Telefon</p>
                      <p className="text-slate-600 text-base">{mockData.companyInfo.phone}</p>
                    </div>
                  </a>
                  <a href="mailto:post@iversendrift.no" className="flex items-start gap-4 group hover:bg-slate-50 p-3 rounded-xl transition-colors">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-blue-600 transition-colors">
                      <Mail className="h-6 w-6 text-blue-600 group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-900 text-lg">E-post</p>
                      <p className="text-slate-600 text-base">{mockData.companyInfo.email}</p>
                    </div>
                  </a>
                  <div className="flex items-start gap-4 p-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-900 text-lg">Lokasjon</p>
                      <p className="text-slate-600 text-base">{mockData.companyInfo.address}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-lg bg-blue-600 text-white border-0">
                <CardContent className="pt-6 pb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Clock className="h-6 w-6" />
                    <h3 className="text-xl font-bold">Åpningstider</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Mandag - Fredag:</span>
                      <span className="font-bold text-lg">08:00 - 17:00</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Lørdag:</span>
                      <span className="font-bold text-lg">10:00 - 14:00</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Søndag:</span>
                      <span className="font-bold text-lg">Stengt</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="shadow-lg border-2 border-blue-200 bg-blue-50">
                <CardContent className="pt-5 pb-5">
                  <div className="flex items-start gap-3">
                    <Sparkles className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-slate-900 mb-1">Raskt svar garantert!</p>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        Vi svarer normalt innen 2 timer på alle henvendelser i åpningstiden.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 md:py-16 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12 mb-10">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-5">
                <Logo className="w-10 h-10" />
                <div>
                  <span className="text-lg font-bold block leading-tight">{mockData.companyInfo.name}</span>
                  <span className="text-xs text-slate-400">Din pålitelige vaktmester</span>
                </div>
              </div>
              <p className="text-slate-400 mb-5 leading-relaxed max-w-md">
                Profesjonelle vaktmester- og vedlikeholdstjenester i Bergen. Vi fikser alt fra småting til større oppdrag med kvalitet og omhu.
              </p>
            </div>
            <div>
              <h3 className="text-base font-bold mb-4 text-white">Tjenester</h3>
              <ul className="space-y-2.5">
                <li><a href="#tjenester" className="text-slate-400 hover:text-white transition-colors text-sm">Reparasjon & Vedlikehold</a></li>
                <li><a href="#tjenester" className="text-slate-400 hover:text-white transition-colors text-sm">Høytrykksvask</a></li>
                <li><a href="#tjenester" className="text-slate-400 hover:text-white transition-colors text-sm">Saltstrøing Vinter</a></li>
                <li><a href="#tjenester" className="text-slate-400 hover:text-white transition-colors text-sm">Låsetjenester</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-base font-bold mb-4 text-white">Kontakt</h3>
              <ul className="space-y-2.5 text-slate-400 text-sm">
                <li><a href="tel:+4712345678" className="hover:text-white transition-colors">{mockData.companyInfo.phone}</a></li>
                <li><a href="mailto:post@iversendrift.no" className="hover:text-white transition-colors">{mockData.companyInfo.email}</a></li>
                <li>{mockData.companyInfo.address}</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-slate-400 text-sm text-center md:text-left">
                © 2025 {mockData.companyInfo.name}. Alle rettigheter reservert.
              </p>
              <div className="flex gap-6 text-sm">
                <a href="#" className="text-slate-400 hover:text-white transition-colors">Personvern</a>
                <a href="#" className="text-slate-400 hover:text-white transition-colors">Vilkår</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
